import React from 'react';
import style from 'acscp/components/flightRequest/form/flightRequestLegHeading.module.scss';
import classNames from 'classnames';
import {
    datePickerFormat,
    FlightRequestFormLeg
} from 'acscp/components/flightRequest/form/helpers/flightRequestFormMapper';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';
import { DateStyle, formatDate, getDateFormat } from 'acscp/components/common/formattedDate';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { ErrorsDisplay } from 'acscp/components/common/errorsDisplay';
import { DateTime } from 'acscp/store';

const translations = defineMessages({
    legContainsErrors: {
        id: 'components.flightRequest.form.flightRequestLegForm.completeMissingFields',
        defaultMessage: 'Please complete missing fields'
    }
});

interface FlightRequestLegHeadingOwnProps {
    leg: FlightRequestFormLeg;
    legNumber: number;
    isFirstLeg: boolean;
    isLastLeg: boolean;
    showLegError: boolean;
    showJourneyLine: boolean;
    onClick: () => void;
}

type FlightRequestLegHeadingProps = FlightRequestLegHeadingOwnProps & InjectedIntlProps;

const renderTimingSummaryOfLeg: React.SFC<FlightRequestLegHeadingProps> = (props: FlightRequestLegHeadingProps) => {
    if (!props.leg.date) {
        return null;
    }

    const formatOptions = getDateFormat(DateStyle.Medium, true);
    const formattedDate = formatDate(props.intl, new DateTime(props.leg.date, datePickerFormat), formatOptions);

    if (props.leg.useDepartureDate) {
        if (!!props.leg.time) {
            return (
                <FormattedMessage
                    id={'components.flightRequest.form.flightRequestLegForm.departAtTimeOnDate'}
                    defaultMessage={'Depart at {formattedTime}, {formattedDate}'}
                    values={{formattedTime: props.leg.time, formattedDate}}
                />
            );
        } else {
            return (
                <FormattedMessage
                    id={'components.flightRequest.form.flightRequestLegForm.departOnDate'}
                    defaultMessage={'Depart on {formattedDate}'}
                    values={{formattedDate}}
                />
            );
        }
    } else {
        if (!!props.leg.time) {
            return (
                <FormattedMessage
                    id={'components.flightRequest.form.flightRequestLegForm.arriveAtTimeOnDate'}
                    defaultMessage={'Arrive at {formattedTime}, {formattedDate}'}
                    values={{formattedTime: props.leg.time, formattedDate}}
                />
            );
        } else {
            return (
                <FormattedMessage
                    id={'components.flightRequest.form.flightRequestLegForm.arriveOnDate'}
                    defaultMessage={'Arrive on {formattedDate}'}
                    values={{formattedDate}}
                />
            );
        }
    }
};

const renderLegSummary: React.SFC<FlightRequestLegHeadingProps> = (props: FlightRequestLegHeadingProps) => {
    const blankRow = '\u00A0';
    return (
        <div className={style.LegSummary}>
            <div className={style.LegSummaryHeader}>
                <div className={style.LegSummaryLegLabel}>
                    <FormattedMessage
                        id={'components.flightRequest.form.flightRequestLegsForm.legNumber'}
                        defaultMessage={'LEG {legNumber, number}'}
                        values={{legNumber: props.legNumber}}
                    />{/* The leg's index uses zero-based numbering so we add one when displaying to the user */}
                </div>
                <div>
                    {renderTimingSummaryOfLeg(props) || blankRow}
                </div>
            </div>
            <div className={style.LegSummaryAirportList}>
                <div className={style.LegSummaryAirport}>
                    <div className={style.LegSummaryAirportCode}>
                        {(!!props.leg.departureAirport && props.leg.departureAirport.iataCode) || blankRow}
                    </div>
                    <div className={style.LegSummaryAirportName}>
                        {(!!props.leg.departureAirport && props.leg.departureAirport.name) || props.leg.departureLocation}
                    </div>
                </div>
                <div className={style.LegSummaryAirport}>
                    <div className={style.LegSummaryAirportCode}>
                        {(!!props.leg.arrivalAirport && props.leg.arrivalAirport.iataCode) || blankRow}
                    </div>
                    <div className={style.LegSummaryAirportName}>
                        {(!!props.leg.arrivalAirport && props.leg.arrivalAirport.name) || props.leg.arrivalLocation}
                    </div>
                </div>
            </div>
            {props.showLegError &&
            <ErrorsDisplay errors={[props.intl.formatMessage(translations.legContainsErrors)]} showUpArrow={true}/>}
        </div>
    );
};

const renderEditingTitle: React.SFC<FlightRequestLegHeadingProps> = (props: FlightRequestLegHeadingProps) => (
    <div className={style.LegHeadingEditTitle}>
        <FormattedMessage
            id={'components.flightRequest.form.flightRequestLegsForm.editingLegNumber'}
            defaultMessage={'Editing leg {legNumber, number}'}
            values={{legNumber: props.legNumber}}
        />
    </div>
);

const FlightRequestLegHeadingWithIntl: React.SFC<FlightRequestLegHeadingProps> = (props: FlightRequestLegHeadingProps) => {
    return (
        <div className={style.LegHeading} onClick={props.onClick}>
            <div className={style.LegHeadingJourneyLine}>
                {props.showJourneyLine &&
                <div className={style.LineContainer}>
                    <div className={style.Dot}/>
                    <div className={classNames(style.Line, {[style.FirstLeg]: props.isFirstLeg, [style.LastLeg]: props.isLastLeg})}/>
                </div>}
            </div>
            <div className={style.LegHeadingTextContainer}>
                {(!props.leg.isExpanded)
                    ? renderLegSummary(props)
                    : renderEditingTitle(props)}
            </div>
            <div className={classNames(style.LegCollapseIndicator, {[style.IsCollapsed]: !props.leg.isExpanded})}>
                <ArrowIcon />
            </div>
        </div>
    );
};

const FlightRequestLegHeading = injectIntl(FlightRequestLegHeadingWithIntl);

export {FlightRequestLegHeading};
